import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { t } from '../i18n';
import { formatDateTime } from '@werkdelta/shared';

/* ————— Speicherstatus («Gespeichert 14:32», Auftrag 8) ————— */

type SaveState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved'; at: Date } | { kind: 'error' };

const SaveCtx = createContext<{ state: SaveState; set: (s: SaveState) => void }>({
  state: { kind: 'idle' },
  set: () => undefined,
});

export function SaveStatusProvider({ children }: { children: ReactNode }) {
  const [state, set] = useState<SaveState>({ kind: 'idle' });
  return <SaveCtx.Provider value={{ state, set }}>{children}</SaveCtx.Provider>;
}

export function useSaveStatus() {
  const { set } = useContext(SaveCtx);
  return {
    saving: () => set({ kind: 'saving' }),
    saved: () => set({ kind: 'saved', at: new Date() }),
    error: () => set({ kind: 'error' }),
  };
}

export function SaveStatusIndicator() {
  const { state } = useContext(SaveCtx);
  if (state.kind === 'idle') return <span className="speicherstatus" aria-live="polite" />;
  if (state.kind === 'saving')
    return (
      <span className="speicherstatus" aria-live="polite">
        {t('app.saving')}
      </span>
    );
  if (state.kind === 'error')
    return (
      <span className="speicherstatus fehler" role="alert">
        {t('app.saveError')}
      </span>
    );
  const time = formatDateTime(state.at).split(', ')[1] ?? '';
  return (
    <span className="speicherstatus" aria-live="polite">
      {t('app.saved', { time })}
    </span>
  );
}

/* ————— InfoTip: Fachbegriff mit Definition (Auftrag 8, Glossar 13.2) ————— */

export function InfoTip({ termKey }: { termKey: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const term = t(`glossary.${termKey}.term`);
  return (
    <span className="infotip" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        aria-label={`${t('glossary.title')}: ${term}`}
        onClick={() => setOpen((o) => !o)}
      >
        i
      </button>
      {open && (
        <span className="infotip-text" role="note" id={id}>
          <strong>{term}:</strong> {t(`glossary.${termKey}.def`)}
        </span>
      )}
    </span>
  );
}

/* ————— Formularfeld ————— */

export function Feld({
  label,
  required,
  error,
  hint,
  termKey,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  termKey?: string;
  children: ReactNode;
}) {
  // Label und Eingabefeld programmatisch verknüpfen (WCAG, Tastaturbedienung)
  const id = useId();
  const errorId = `${id}-fehler`;
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errorId : undefined,
      })
    : children;
  return (
    <div className={`feld${error ? ' hat-fehler' : ''}`}>
      <label htmlFor={id}>
        {label}
        {required && (
          <span className="pflicht" aria-label={t('common.required')}>
            *
          </span>
        )}
        {termKey && <InfoTip termKey={termKey} />}
      </label>
      {hint && <p className="erklaerung">{hint}</p>}
      {control}
      {error && (
        <p className="feld-fehler" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/* ————— Hinweisblock mit Label «keine Rechtsberatung» ————— */

export function RechtsHinweis({ text }: { text: string }) {
  return (
    <div className="hinweis">
      <span className="label">{t('app.legal.noAdvice')}</span>
      <div>{text}</div>
    </div>
  );
}

/* ————— Bestätigungsdialog (kein stilles Verwerfen, Auftrag 8) ————— */

export function useConfirm(): {
  confirm: (frage: string, gefahr?: boolean) => Promise<boolean>;
  dialog: ReactNode;
} {
  const [frage, setFrage] = useState<string | null>(null);
  const [gefahr, setGefahr] = useState(false);
  const resolver = useRef<(v: boolean) => void>();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const confirm = useCallback((q: string, g = false) => {
    setFrage(q);
    setGefahr(g);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);
  const done = (v: boolean) => {
    resolver.current?.(v);
    setFrage(null);
  };
  // Als echtes modales Overlay öffnen (showModal): zentriert mit Abdunklung,
  // unabhängig von der Scroll-Position sichtbar (statt inline im Seitenfluss).
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (frage !== null && !el.open) el.showModal();
    if (frage === null && el.open) el.close();
  }, [frage]);
  const dialog = (
    <dialog
      ref={dialogRef}
      className="wd-dialog"
      aria-modal="true"
      onCancel={(e) => {
        e.preventDefault();
        done(false);
      }}
    >
      {frage !== null && (
        <>
          <p>{frage}</p>
          <div className="aktionen">
            <button
              type="button"
              className={gefahr ? 'knopf-gefahr' : 'knopf-primaer'}
              onClick={() => done(true)}
            >
              {t('common.confirm')}
            </button>
            <button type="button" className="knopf-sekundaer" onClick={() => done(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}
    </dialog>
  );
  return { confirm, dialog };
}

/* ————— Fehleranzeige (zwei Teile: was passiert ist, was zu tun ist) ————— */

export function Fehler({ code }: { code: string | null }) {
  if (!code) return null;
  const key = `apiError.${code}`;
  return (
    <div className="fehlermeldung" role="alert">
      {hasTranslation(key) ? t(key) : `${t('error.generic')} (${code})`}
    </div>
  );
}

import { hasKey as hasTranslation } from '../i18n';
